import { useState } from 'react';
import {
  AI_ACT_EXPORT_PATH,
  AUDIT_EXPORT_CSV_PATH,
  AUDIT_EXPORT_JSON_PATH,
  aiActExport,
  frameworkDisable,
  frameworkEnable,
  frameworks as frameworksRoute,
  frameworksEnabled,
  type ApiError,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { Skeleton } from '../../ui/states';
import { ErrLine, I, btn, chip, downloadFrom, fig, notServed, sx, tileWrap, useApi, useGuarded, usePage } from './kit';
import { useCustomerText } from '../../ui/buildLabels';

const pct = (x: number) => (Math.round(x * 1000) / 10).toFixed(1) + '%';

/** Compliance (design seIsCompliance, T8980; FW and the tiles, L21906 and L22444-22487). */
export function ComplianceSection() {
  const plain = useCustomerText();
  const { client } = useSession();
  const { changed } = usePage();
  const { wr } = useGuarded();
  const fws = useApi((c, s) => frameworksRoute(c, { signal: s }), []);
  const enabledQ = useApi((c, s) => frameworksEnabled(c, { signal: s }), []);
  const ai = useApi((c, s) => aiActExport(c, { signal: s }), []);
  const [justEnabled, setJustEnabled] = useState<Record<string, boolean>>({});
  const [exported, setExported] = useState<string | null>(null);
  const [packDone, setPackDone] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);

  if (!fws.data && !fws.error) return <Skeleton rows={6} label="Loading compliance" />;

  const FW = fws.data?.frameworks ?? [];
  const known = !!enabledQ.data;
  const onSet = new Set([...(enabledQ.data?.enabled ?? []), ...Object.keys(justEnabled).filter((k) => justEnabled[k])]);
  Object.entries(justEnabled).forEach(([k, v]) => { if (!v) onSet.delete(k); });
  const fwOn = (id: string) => onSet.has(id);
  const byName = (n: string) => FW.find((f) => f.name === n || f.id === n);
  const euOn = !!byName('EU AI Act') && fwOn(byName('EU AI Act')!.id);
  const hipaaOn = !!byName('HIPAA') && fwOn(byName('HIPAA')!.id);
  const fwCount = FW.filter((f) => fwOn(f.id)).length;
  const policyCount = FW.filter((f) => fwOn(f.id)).reduce((n, f) => n + f.policyCount, 0);
  const d = ai.data;
  const by = d?.byRiskClass ?? {};
  const high = (by.HIGH ?? 0) + (by.UNACCEPTABLE ?? 0);
  const dash = '–';

  const tiles: [string, string, string, string][] = [
    ['Frameworks enabled', (known || Object.keys(justEnabled).length ? fwCount : dash) + '/' + FW.length, known ? policyCount + ' policies auto-created' : plain('enabled list not served yet'), fwCount ? '#1F6B52' : '#22272E'],
    ['Decisions classified', d ? String(d.totalDecisions) : dash, 'every action', '#22272E'],
    ['High-risk records', d ? String(high) : dash, 'Art 6 · logged', '#22272E'],
    ['Human override rate', d ? pct(d.humanOverrideRate) : dash, 'Art 14', '#5B6B7C'],
    ['Conformity', euOn ? 'READY' : 'NOT CLAIMED', 'export anytime', euOn ? '#1F6B52' : '#54606C'],
  ];
  const RISK: [string, string, string, number | null, string, string][] = [
    ['UNACCEPTABLE', 'Unacceptable risk', 'prohibited, auto-blocked at the gate', d ? by.UNACCEPTABLE ?? 0 : null, '#B3261E', 'rgba(179,38,30,.14)'],
    ['HIGH', 'High risk', 'Art 6 · AI serving vulnerable people', d ? by.HIGH ?? 0 : null, '#5B6B7C', 'rgba(91,107,124,.18)'],
    ['LIMITED', 'Limited risk', 'transparency obligations met', d ? by.LIMITED ?? 0 : null, '#264C7E', 'rgba(79,138,214,.16)'],
    ['MINIMAL', 'Minimal risk', 'standard logging', d ? by.LOW ?? 0 : null, '#12503C', 'rgba(47,138,111,.16)'],
  ];
  const EXPORTS: [string, string, string, string][] = [
    ['Export conformity assessment', 'JSON', AI_ACT_EXPORT_PATH, 'eu-ai-act-conformity.json'],
    ['Audit export', 'JSON', AUDIT_EXPORT_JSON_PATH, 'audit-log.json'],
    ['Audit export', 'CSV', AUDIT_EXPORT_CSV_PATH, 'audit-log.csv'],
  ];
  const run = async (fn: () => Promise<void>) => {
    setErr(null);
    try {
      await fn();
    } catch (e) {
      setErr(e as ApiError);
    }
  };

  return (
    <div data-testid="settings-compliance">
      <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:13px;flex-wrap:wrap;row-gap:8px;')}>
        <span style={sx('display:inline-flex;align-items:center;gap:8px;font-size:16px;border-radius:9px;padding:7px 12px;flex:0 0 auto;' + (euOn ? 'color:#12503C;background:rgba(47,138,111,.12);border:1px solid rgba(47,138,111,.3);' : 'color:#54606C;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);'))}>
          <span style={sx('width:7px;height:7px;border-radius:50%;background:' + (euOn ? '#1F6B52' : '#54606C') + ';')} />
          {I('EU AI Act · ' + (euOn ? 'enforced live' : 'not enabled'))}
        </span>
        <span style={sx('display:inline-flex;align-items:center;gap:8px;font-size:16px;border-radius:9px;padding:7px 12px;flex:0 0 auto;' + (hipaaOn ? 'color:#12503C;background:rgba(47,138,111,.12);border:1px solid rgba(47,138,111,.3);' : 'color:#54606C;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);'))}>
          <span style={sx('width:7px;height:7px;border-radius:50%;background:' + (hipaaOn ? '#1F6B52' : '#54606C') + ';')} />
          {I('HIPAA · HITECH · SOC 2 ' + (hipaaOn ? 'mapped' : 'not mapped'))}
        </span>
        <span style={sx('flex:1;')} />
        <a href="#" onClick={(e) => { e.preventDefault(); void run(async () => { await downloadFrom(client, AI_ACT_EXPORT_PATH, 'eu-ai-act-conformity.json'); setPackDone(true); }); }} style={sx(btn(packDone ? 'soft' : 'ghost'))}>
          {I(packDone ? 'Pack exported' : 'Conformity pack')}
        </a>
      </div>
      <div style={sx('grid-column:1/-1;display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:10px 18px;box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);border-bottom:1px solid rgba(34,39,46,.14);padding:10px 0;margin-bottom:12px;')}>
        {tiles.map((t, i, a) => (
          <div key={t[0]} style={sx(tileWrap(i, a.length))}>
            <div style={sx('font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#54606C;margin-bottom:6px;')}>{I(t[0])}</div>
            <div style={sx(fig(t[3], t[1].length > 6 ? 19 : 26))}>{I(t[1])}</div>
            <div style={sx('font-size:16px;color:#54606C;margin-top:6px;')}>{I(t[2])}</div>
          </div>
        ))}
      </div>
      <ErrLine error={err ?? (ai.error && !notServed(ai.error) ? ai.error : null)} style="margin:-4px 0 10px;" />
      <div style={sx('display:grid;grid-template-columns:minmax(0,1fr) 340px;gap:12px;align-items:start;')}>
        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:9px 2px;min-width:0;')}>
          <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:12px;')}>
            <span style={sx('width:7px;height:7px;border-radius:50%;background:#2F5C97;')} />
            <span style={sx('font-size:16px;font-weight:700;')}>Regulatory frameworks</span>
            <span style={sx('flex:1;')} />
            <span style={sx('font-size:16px;color:#54606C;')}>{I(known ? fwCount + ' enabled · ' + (FW.length - fwCount) + ' available' : FW.length + ' available')}</span>
          </div>
          <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:11px;')}>
            {FW.map((f) => {
              const on = fwOn(f.id);
              return (
                <div key={f.id} style={sx('box-sizing:border-box;border-radius:10px;padding:12px 14px;background:#FBF8F1;border:1px solid ' + (on ? 'rgba(47,138,111,.34);' : 'rgba(34,39,46,.11);'))}>
                  <div style={sx('display:flex;align-items:flex-start;gap:9px;')}>
                    <span style={sx('display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;flex:0 0 18px;border-radius:5px;font-size:16px;font-weight:700;margin-top:1px;white-space:nowrap;' + (on ? 'background:#1F6B52;color:#fff;' : 'background:#FCFAF4;border:1px solid rgba(34,39,46,.24);color:transparent;'))}>{I(on ? '✓' : '')}</span>
                    <span style={sx('min-width:min(100%,130px);flex:1 1 130px;')}>
                      <span style={sx('display:block;font-size:16px;font-weight:700;color:#22272E;')}>{I(f.name)}</span>
                      <span style={sx('display:block;font-size:16px;color:#54606C;margin-top:2px;')}>{I(f.region)}</span>
                    </span>
                  </div>
                  <div style={sx('font-size:16px;color:#54606C;line-height:1.5;margin-top:8px;min-height:44px;')}>{I(f.description)}</div>
                  <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-top:9px;')}>
                    <span style={sx('font-size:16px;color:#54606C;')}>{I(f.policyCount + ' policies')}</span>
                    <span style={sx('flex:1;')} />
                    <a
                      href="#"
                      aria-pressed={on}
                      {...wr(() => void run(async () => {
                        if (on) await frameworkDisable(client, { params: { id: f.id }, body: { confirm: true } });
                        else await frameworkEnable(client, { params: { id: f.id }, body: { confirm: true } });
                        setJustEnabled((p) => ({ ...p, [f.id]: !on }));
                        enabledQ.reload();
                        changed();
                      }))}
                      style={sx(btn(on ? 'soft' : 'primary'))}
                    >
                      {I(on ? 'Enabled' : 'Enable')}
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:9px 2px;min-width:0;')}>
          <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:12px;')}>
            <span style={sx('width:7px;height:7px;border-radius:50%;background:#B3261E;')} />
            <span style={sx('font-size:16px;font-weight:700;')}>EU AI Act &middot; decision records</span>
            <span style={sx('flex:1;')} />
            <span style={sx('font-size:16px;color:#54606C;')}>Art 6 &middot; 12 &middot; 14</span>
          </div>
          {RISK.map((r) => (
            <div key={r[0]} style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;padding:9px 0;border-top:1px solid rgba(34,39,46,.07);')}>
              <span style={sx(chip(r[4], r[5]))}>{I(r[0])}</span>
              <span style={sx('min-width:min(100%,130px);flex:1 1 130px;')}>
                <span style={sx('display:block;font-size:16px;font-weight:600;color:#22272E;')}>{I(r[1])}</span>
                <span style={sx('display:block;font-size:16px;color:#54606C;margin-top:2px;')}>{I(r[2])}</span>
              </span>
              <span style={sx('font-size:16px;font-weight:700;color:#22272E;flex:0 1 auto;min-width:0;')}>{I(r[3] === null ? dash : String(r[3]))}</span>
            </div>
          ))}
          <div style={sx('display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:11px;margin-top:13px;padding-top:12px;border-top:1px solid rgba(34,39,46,.1);')}>
            <div>
              <div style={sx('font-size:19px;font-weight:700;color:#22272E;')}>{I(d ? String(high) : dash)}</div>
              <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-top:4px;')}>High-risk decisions</div>
            </div>
            <div>
              <div style={sx('font-size:19px;font-weight:700;color:#1F6B52;')}>{I(d ? pct(d.humanOverrideRate) : dash)}</div>
              <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-top:4px;')}>Override rate</div>
            </div>
          </div>
          <div style={sx('display:flex;flex-direction:column;gap:8px;margin-top:14px;')}>
            {EXPORTS.map((x) => {
              const done = exported === x[0] + x[1];
              return (
                <a
                  key={x[0] + x[1]}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    void run(async () => {
                      await downloadFrom(client, x[2], x[3]);
                      setExported(x[0] + x[1]);
                    });
                  }}
                  style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;padding:10px 12px;border-radius:9px;font-size:16px;font-weight:600;text-decoration:none;' + (done ? 'background:rgba(47,138,111,.12);color:#12503C;border:1px solid rgba(47,138,111,.34);' : 'background:#FBF8F1;color:#3C454F;border:1px solid rgba(34,39,46,.13);'))}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={sx('flex:0 0 14px;')} aria-hidden="true">
                    <path d="M12 3.6v11" />
                    <path d="m7.6 10.2 4.4 4.4 4.4-4.4" />
                    <path d="M4.4 20.4h15.2" />
                  </svg>
                  {I(x[0])}
                  <span style={sx('flex:1;')} />
                  <span style={sx('font-size:16px;color:#54606C;')}>{I(x[1])}</span>
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
